// 261010 P9Q (FE) — «AI chấm là một bước chờ». THUẦN (không antd / router): luật chờ + góp ý còn lại của 6 bước có chấm.
// Chờ = chưa duyệt được (KHÔNG phải lỗi). Kết thúc (PASS hoặc máy hết sức) = không chặn; phần còn lại chỉ là góp ý tham khảo, đọc-chỉ.
// Nguồn: `status.qualityByStep[STEP].activity/openIssueCount/openIssues` + `stepCapabilities[STEP].reason === 'QUALITY_RUNNING'` (BE mới).
// BE cũ thiếu khoá ⇒ suy từ `run.active` / `items[].phase` (PENDING·SCORING·REVIEWING·RESCORING ⇒ đang chấm, REWRITING ⇒ đang sửa); không vỡ.
import type { WorkflowStatus, WorkflowStepKey } from '../../types/filmWorkflow';
import type { WorkflowQualityActivity, WorkflowQualityItem, WorkflowQualitySignal } from './workflowQualityPolicy';
import { qualityResultText } from './workflowQualityPolicy';

export const QUALITY_WAIT_REASON = 'QUALITY_RUNNING';
/** 29/10: BE chỉ chấm một lần rồi sửa tối đa một lần ⇒ mặc định khi BE không gửi `maxRounds` là 1 (BE cũ luôn gửi khoá này). */
export const QUALITY_MAX_ROUNDS = 1;
/** Sáu bước có chấm. */
export const QUALITY_STEP_KEYS: WorkflowStepKey[] = ['SCRIPT', 'DIRECTING', 'CINEMATOGRAPHY', 'MAP', 'STORYBOARD', 'PROMPT'];

const REVIEW_PHASES = new Set(['PENDING', 'SCORING', 'REVIEWING', 'RESCORING']);
const FIX_PHASES = new Set(['REWRITING']);
/** Kết quả mà máy đã hết sức hoặc chưa có gì để sửa: góp ý còn lại tính ở đây (PASS = 0). */
const ADVISORY_RESULTS = new Set(['FIXED', 'FIX_REQUIRED', 'NEEDS_HUMAN', 'INCOMPLETE', 'UNCERTAIN', 'CAP_REACHED', 'NO_PROGRESS']);

const IDLE: WorkflowQualityActivity = { kind: 'IDLE', round: 0, maxRounds: QUALITY_MAX_ROUNDS };

const clampRound = (n: unknown, max: number): number => { const v = Number(n); return Number.isFinite(v) ? Math.min(Math.max(Math.trunc(v), 1), max) : 1; };

/** Hoạt động chấm/sửa của MỘT bước. `cap` = `stepCapabilities[step]` (lý do QUALITY_RUNNING cũng tính là đang chờ). Không có tín hiệu nào ⇒ IDLE. */
export function qualityActivityOf(signal: WorkflowQualitySignal | undefined | null, cap?: { reason?: string } | null): WorkflowQualityActivity {
  const a = signal?.activity;
  if (a && (a.kind === 'IDLE' || a.kind === 'REVIEWING' || a.kind === 'FIXING')) {
    const max = Number.isFinite(Number(a.maxRounds)) && Number(a.maxRounds) > 0 ? Math.trunc(Number(a.maxRounds)) : QUALITY_MAX_ROUNDS;
    if (a.kind !== 'IDLE') return { kind: a.kind, round: a.kind === 'FIXING' ? clampRound(a.round, max) : Math.max(0, Math.trunc(Number(a.round) || 0)), maxRounds: max };
    // BE đã nói rảnh; chỉ lý do QUALITY_RUNNING còn sót ở capability (đọc lệch nhịp) mới giữ trạng thái chờ.
    return cap?.reason === QUALITY_WAIT_REASON ? { kind: 'REVIEWING', round: 0, maxRounds: max } : { ...IDLE, maxRounds: max };
  }
  // ── BE cũ: suy từ phase của từng mục / run.active ──
  const items: WorkflowQualityItem[] = signal?.items ?? [];
  const phases = items.map((i) => String(i.phase ?? '').toUpperCase());
  if (phases.some((p) => FIX_PHASES.has(p))) {
    const done = items.reduce((m, i) => Math.max(m, Number(i.autoRewriteCount) || 0), 0);
    return { kind: 'FIXING', round: clampRound(done + 1, QUALITY_MAX_ROUNDS), maxRounds: QUALITY_MAX_ROUNDS };
  }
  if (phases.some((p) => REVIEW_PHASES.has(p)) || signal?.run?.active === true || cap?.reason === QUALITY_WAIT_REASON) return { kind: 'REVIEWING', round: 0, maxRounds: QUALITY_MAX_ROUNDS };
  return IDLE;
}

/** Câu chờ (đúng chữ ở đầu mục bước, nút chính và nút Duyệt bị khoá). */
export function qualityWaitText(a: WorkflowQualityActivity): string {
  // Chỉ một lần sửa (maxRounds=1) ⇒ «vòng 1/1» là thừa; BE cũ vẫn gửi 3 ⇒ giữ «vòng k/3».
  if (a.kind !== 'FIXING') return 'AI đang chấm…';
  return a.maxRounds <= 1 ? 'AI đang sửa…' : `AI đang sửa (vòng ${clampRound(a.round, a.maxRounds)}/${a.maxRounds})…`;
}

export interface QualityWait { waiting: boolean; kind: WorkflowQualityActivity['kind']; text: string | null; activity: WorkflowQualityActivity }

/** Bước đang chờ AI chấm/sửa? Bước không thuộc 6 bước có chấm ⇒ không bao giờ chờ. */
export function qualityWaitOf(s: WorkflowStatus | null | undefined, step: WorkflowStepKey): QualityWait {
  if (!QUALITY_STEP_KEYS.includes(step)) return { waiting: false, kind: 'IDLE', text: null, activity: IDLE };
  const activity = qualityActivityOf(s?.qualityByStep?.[step], s?.stepCapabilities?.[step]);
  return activity.kind === 'IDLE' ? { waiting: false, kind: 'IDLE', text: null, activity } : { waiting: true, kind: activity.kind, text: qualityWaitText(activity), activity };
}

/** Còn bước nào đang chấm/sửa ⇒ trang phải tiếp tục poll (điều kiện làm tươi 8 s). */
export function anyQualityBusy(s: WorkflowStatus | null | undefined): boolean {
  return QUALITY_STEP_KEYS.some((k) => qualityWaitOf(s, k).waiting);
}

export interface QualityAdvisoryLine { id: string; criterion: string; videos: number[]; text: string }
export interface QualityAdvisory { count: number; lines: QualityAdvisoryLine[]; /** Tổng số góp ý nhiều hơn số dòng hiện được (BE tối đa 20). */ truncated: boolean }

const orderOf = (s: WorkflowStatus | null | undefined): Map<string, number> => new Map((s?.events ?? []).map((e) => [e.eventId, e.eventOrder]));
const videosOf = (ids: unknown, order: Map<string, number>): number[] => {
  const out: number[] = [];
  for (const id of Array.isArray(ids) ? ids : []) { const n = order.get(String(id)); if (n != null && !out.includes(n)) out.push(n); }
  return out.sort((a, b) => a - b);
};

/**
 * Góp ý còn lại (chỉ-đọc) của bước đã chấm xong. Đang chờ ⇒ null (chưa có gì để hiện). 0 góp ý ⇒ null.
 * Có `openIssues` ⇒ dùng nguyên văn (criterion · video · text). Thiếu (BE cũ) ⇒ đếm `items[].issueIds` của mục chưa PASS và mỗi mục một dòng (chữ = kết quả chấm).
 */
export function qualityAdvisoryOf(s: WorkflowStatus | null | undefined, step: WorkflowStepKey): QualityAdvisory | null {
  if (!QUALITY_STEP_KEYS.includes(step)) return null;
  const signal = s?.qualityByStep?.[step];
  if (!signal || qualityWaitOf(s, step).waiting) return null;
  const order = orderOf(s);
  if (Array.isArray(signal.openIssues)) {
    const lines = signal.openIssues.slice(0, 20).map((i) => ({ id: String(i.id), criterion: String(i.criterion ?? ''), videos: videosOf(i.eventIds, order), text: String(i.text ?? '') }));
    const count = Math.max(Number.isFinite(Number(signal.openIssueCount)) ? Math.trunc(Number(signal.openIssueCount)) : 0, lines.length);
    return count > 0 ? { count, lines, truncated: count > lines.length } : null;
  }
  if (typeof signal.openIssueCount === 'number' && signal.openIssueCount <= 0) return null;
  const rows = (signal.items ?? []).filter((i) => ADVISORY_RESULTS.has(i.result) && (i.issueIds?.length ?? 0) > 0);
  const lines = rows.map((i) => ({ id: i.subjectKey, criterion: '', videos: videosOf(i.eventIds, order), text: qualityResultText(i.result) }));
  const derived = rows.reduce((n, i) => n + (i.issueIds?.length ?? 0), 0);
  const count = typeof signal.openIssueCount === 'number' ? Math.trunc(signal.openIssueCount) : derived;
  return count > 0 ? { count, lines, truncated: false } : null;
}

/** «video 3, 5» / «video 3, 5, 8 +2» — rỗng ⇒ ''. */
export function videosText(videos: number[], max = 6): string {
  if (!videos.length) return '';
  const head = videos.slice(0, max).join(', ');
  return `video ${head}${videos.length > max ? ` +${videos.length - max}` : ''}`;
}
